import { useState, useMemo, useEffect } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import TuneIcon from "@mui/icons-material/Tune";
import { AgGridReact } from "ag-grid-react";
import Card, { CARD_HOVER_SX, CARD_HOVER_TRANSITION } from "@plannel/components/dashboard/DashboardCard";
import DashboardCardHeader from "@plannel/components/dashboard/DashboardCardHeader";
import { DashboardPage, DashboardTopBar, DashboardRow } from "@plannel/components/dashboard/DashboardLayout";
import DashboardLayoutModal from "@plannel/components/dashboard/DashboardLayoutModal";
import { KpiCard } from "@plannel/components/dashboard/KpiCard";
import CompactSelect from "@plannel/components/dashboard/CompactSelect";
import { DashboardComposedChart, barOrLine } from "@plannel/components/dashboard/DashboardChart";
import { dashboardDefaultColDef, dashboardGridSx, DASHBOARD_ROW_HEIGHT } from "@plannel/components/dashboard/dashboardGrid";
import { makeFmtMoney, fmtQty, fmtExact } from "@plannel/components/dashboard/dashboardFormat";
import useDashboardData from "@plannel/hooks/useDashboardData";
import useDashboardP13n, { MAX_TOP_N } from "@plannel/hooks/useDashboardP13n";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import supplyTrendService from "@plannel/services/dashboard/supply-trend-service";
import reduxUtil from "@plannel/utils/redux-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import { FS } from "@plannel/utils/font-util";

/*
 * Supply Trend — 선적 → 운송 → 도착 구간의 성과.
 * 산식·설계 근거: saas-application/docs/sql/dw/dashboard-spec/08_supply_trend.md
 *
 * 기존 7 화면은 계획과 잔량만 본다. 이 화면만 **운송 구간**을 맡는다(mj 정시율 36.5%).
 */

const CARD_DEFS = [
  { key: "delaySplit", span: 3, minH: 260 },
  { key: "lanes", span: 3, minH: 260 },
  // 품목(무엇)과 진행 중(지금)을 한 행에 — 둘 다 앞 행의 "왜"·"어디" 와 다른 질문이다
  { key: "items", span: 3, minH: 260 },
  { key: "inflight", span: 3, minH: 260 },
  { key: "trend", span: 6, minH: 280 },
];
const DEFAULT_LAYOUT = { order: CARD_DEFS.map((c) => c.key), hidden: {} };

/*
 * 드릴 대상은 **출하 실적 하나**다.
 *   이 화면은 **선적** 축인데 주문 추적은 **주문** 축이고, 주문 테이블에는 지연 컬럼이 아예 없다
 *   (`z_mp_order`·`z_sales_order` 확인). 주문으로 보내면 "왜 늦었나" 의 맥락이 끊긴다.
 *   예전엔 제목·KPI 가 전부 주문 추적으로 가면서 **파라미터도 안 넘겼다** — 드릴이 아니라 바로가기였다.
 */
const DRILL = { shipmentTxn: "INPUT_SHIPMENT_TXN" };

const SERIES = {
  onTime: ColorUtil.colorSet(0),
  transit: ColorUtil.colorSet(2),
};

// ─────────────────────────────────────────
// 지연 원인 분해 — 이 화면의 핵심 카드
// ─────────────────────────────────────────
/*
 * 네 갈래는 **대응 부서가 다르다**:
 *   출발 늦음 → 출고·생산 프로세스 / 운송 지연 → 캐리어·경로.
 *   정시율 한 숫자로는 이 둘을 구분할 수 없다(명세 §2).
 * 색: 정시는 성공색, 만회는 중립, 지연 둘은 경고·위험으로 **심각도 순**.
 * 네 갈래의 순서·누락은 **서버가 보장한다**(없는 갈래도 0 으로 채워 내린다) — 프런트는 받은 대로 그린다.
 */
const DelaySplitCard = ({ title, rows, drill, onBucketDrill }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const color = {
    onTime: theme.palette.success.main,
    depRecovered: theme.palette.info.main,
    transitLate: theme.palette.warning.main,
    bothLate: theme.palette.error.main,
  };
  const total = (rows ?? []).reduce((s, r) => s + (r.count ?? 0), 0);

  /*
   * 숫자 세 칸의 **폭을 고정**한다 — 머리글과 아래 줄이 같은 폭을 써야 "어느 숫자가 무엇인지" 가
   *   세로로 맞는다. 폭이 가변이면 머리글을 붙여도 값과 어긋나 오히려 헷갈린다.
   */
  const COL = { transit: 52, count: 48, pct: 44 };
  const numSx = (w) => ({ minWidth: w, textAlign: "right" });

  return (
    <>
      <DashboardCardHeader title={title} dense {...drill} />
      {/*
        갈래는 **네 개로 고정**이라 넘칠 일이 없다 → `overflow: auto` 를 두지 않는다.
          스크롤 막대가 생기면 "더 있는데 잘렸나" 로 읽혀 없는 정보를 암시한다.
        대신 `space-between` 으로 세로를 채운다 — 카드가 길어졌을 때 막대가 위에만 몰리면
          아래가 빈 채로 남는다(카드 높이는 행 트랙이 정하므로 내용이 짧아도 늘어난다).
      */}
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column",
                 justifyContent: "space-between", gap: 1, pt: 0.5, pb: 0.5 }}>
        {/* 머리글 — 숫자만 있으면 11.4d / 99 / 7.2% 가 각각 무엇인지 읽는 사람이 알 수 없다 */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "baseline", gap: 1 }}>
          <Typography sx={{ fontSize: FS.CAPTION, color: "text.disabled", ...numSx(COL.transit) }}>
            {t("stAvgTransit")}
          </Typography>
          <Typography sx={{ fontSize: FS.CAPTION, color: "text.disabled", ...numSx(COL.count) }}>
            {t("stShipments")}
          </Typography>
          <Typography sx={{ fontSize: FS.CAPTION, color: "text.disabled", ...numSx(COL.pct) }}>
            {t("spColShare")}
          </Typography>
        </Box>
        {(rows ?? []).map((r) => {
          const pct = total > 0 ? (r.count / total) * 100 : 0;
          /*
            갈래 한 줄 전체가 링크다 — 막대만 누르게 하면 표본이 작은 갈래(정시 7.2%)는
              막대가 너무 짧아 과녁이 안 된다. 라벨·숫자까지 같은 과녁으로 묶는다.
            권한이 없으면 `onBucketDrill` 이 아예 안 와서 커서·hover 가 같이 사라진다
              — 눌리지 않는데 눌릴 것처럼 보이는 상태를 만들지 않는다.
          */
          const clickable = !!onBucketDrill;
          return (
            <Box key={r.bucket}
              onClick={clickable ? () => onBucketDrill(r.bucket) : undefined}
              sx={clickable ? {
                cursor: "pointer", borderRadius: 1, px: 0.5, mx: -0.5,
                transition: "background-color 0.15s",
                "&:hover": { bgcolor: "action.hover" },
              } : null}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 0.3 }}>
                <Typography sx={{ fontSize: FS.CAPTION, color: "text.primary" }}>
                  {t(`stBucket_${r.bucket}`)}
                </Typography>
                <Box sx={{ display: "flex", gap: 1, alignItems: "baseline" }}>
                  <Typography sx={{ fontSize: FS.CAPTION, color: "text.secondary", ...numSx(COL.transit) }}>
                    {r.avgTransit == null ? "-" : `${r.avgTransit}${t("invDaysUnit")}`}
                  </Typography>
                  {/* 건수는 **합이 도착 건수와 맞아야 하는 수**라 축약하지 않는다(네 갈래 합 = 모수) */}
                  <Typography sx={{ fontSize: FS.BODY, fontWeight: 700, color: "text.primary", ...numSx(COL.count) }}>
                    {fmtExact(r.count)}
                  </Typography>
                  <Typography sx={{ fontSize: FS.CAPTION, color: "text.secondary", ...numSx(COL.pct) }}>
                    {pct.toFixed(1)}%
                  </Typography>
                </Box>
              </Box>
              {/* 비율 막대 — 네 갈래의 크기 비교가 이 카드의 요점이다 */}
              <Box sx={{ height: 8, borderRadius: 1, bgcolor: alpha(theme.palette.text.primary, 0.06) }}>
                <Box sx={{ width: `${pct}%`, height: "100%", borderRadius: 1, bgcolor: color[r.bucket] }} />
              </Box>
            </Box>
          );
        })}
      </Box>
    </>
  );
};

// ─────────────────────────────────────────
// Lane 성과
// ─────────────────────────────────────────
/*
 * 정렬축 — **운송일수와 계획대비는 순위가 뒤집힌다**(명세 §3).
 *   절대 1 위 중부DC→US West DC(1.9 일 격차, 계획대비 109%) vs
 *   계획대비 1 위 중부DC→서울점(1.2 일 격차, **219%**). 단거리 lane 은 계획이 1~2 일로
 *   타이트해서 같은 1 일 지연이 두 배가 된다 — 어느 한쪽만 주면 관점 하나가 사라진다.
 *   서버가 **두 축의 상위를 합쳐서** 내리므로 어느 축을 골라도 상위가 채워진다.
 */
const LANE_SORT = {
  transit: (a, b) => (b.avgTransit ?? -1) - (a.avgTransit ?? -1),
  ltRatio: (a, b) => (b.ltRatio ?? -1) - (a.ltRatio ?? -1),
};

/*
 * 소수 1 자리 **고정**. 서버가 `round1` 로 보내면 12.0 은 JSON 에서 `12` 가 되고 그대로 찍으면
 *   한 열에 "220%" 와 "186.1%" 가, "3일" 과 "3.7일" 이 섞인다 — 자릿수가 들쭉날쭉하면
 *   열을 위아래로 훑어 비교할 수 없다. 표의 숫자 열은 자릿수를 맞춘다.
 */
const d1 = (v) => (v == null ? null : Number(v).toFixed(1));

const LanesCard = ({ title, rows, topN, onChangeTopN, drill, onRowDrill }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [sortBy, setSortBy] = useState("transit");
  const shown = useMemo(
    () => [...(rows ?? [])].sort(LANE_SORT[sortBy]).slice(0, topN),
    [rows, topN, sortBy],
  );

  const columnDefs = useMemo(() => [
    { headerName: t("stLane"), field: "lane", flex: 1, minWidth: 150,
      valueGetter: (p) => `${p.data?.origin ?? ""} → ${p.data?.dest ?? ""}` },
    { headerName: t("stShipments"), field: "shipments", width: 66, type: "rightAligned",
      valueFormatter: (p) => (p.value ?? 0).toLocaleString() },
    { headerName: t("stAvgTransit"), field: "avgTransit", width: 80, type: "rightAligned",
      valueFormatter: (p) => (p.value == null ? "-" : `${d1(p.value)}${t("invDaysUnit")}`) },
    /*
      계획 LT 는 **분모라서 항상 보인다** — 비율만 주면 "219%" 가 몇 일에서 몇 일로 늘어난
      219% 인지 알 수 없다(PSI Balance 에서 목표재고 컬럼을 상시 노출한 것과 같은 이유).
    */
    { headerName: t("stPlannedLt"), field: "plannedLt", width: 74, type: "rightAligned",
      valueFormatter: (p) => (p.value == null ? "-" : `${d1(p.value)}${t("invDaysUnit")}`) },
    { headerName: t("stLtRatio"), field: "ltRatio", width: 82, type: "rightAligned",
      valueFormatter: (p) => (p.value == null ? "-" : `${d1(p.value)}%`),
      // 계획을 넘긴 lane 을 경고색으로. 150% 이상이면 계획이 현실과 한참 다르다는 뜻이라 더 진하게
      cellStyle: (p) => ({
        color: p.value == null ? theme.palette.text.secondary
          : p.value >= 150 ? theme.palette.error.dark
          : p.value > 100 ? theme.palette.warning.dark : theme.palette.text.primary,
        fontWeight: (p.value ?? 0) >= 150 ? 700 : 400, textAlign: "right",
      }) },
    { headerName: t("stOnTimePct"), field: "onTimePct", width: 78, type: "rightAligned",
      valueFormatter: (p) => (p.value == null ? "-" : `${d1(p.value)}%`),
      // 정시율은 **낮을수록 나쁘다** — 50% 미만을 위험색으로 칠해 눈에 띄게 한다
      cellStyle: (p) => ({
        color: (p.value ?? 100) < 50 ? theme.palette.error.dark : theme.palette.text.primary,
        fontWeight: (p.value ?? 100) < 50 ? 700 : 400, textAlign: "right",
      }) },
  ], [t, theme]);

  const topNOptions = Array.from({ length: MAX_TOP_N }, (_, i) => ({ value: i + 1, label: `Top ${i + 1}` }));
  const sortOptions = useMemo(() => [
    { value: "transit", label: t("stSortTransit") },
    { value: "ltRatio", label: t("stSortLtRatio") },
  ], [t]);

  return (
    <>
      <DashboardCardHeader title={title} dense {...drill}>
        <CompactSelect value={sortBy} options={sortOptions}
          onChange={setSortBy} minWidth={96} />
        <CompactSelect value={topN} options={topNOptions} onChange={onChangeTopN} minWidth={78} />
      </DashboardCardHeader>
      <Box className="ag-theme-balham" sx={dashboardGridSx(theme, !!onRowDrill)}>
        <AgGridReact
          theme="legacy"
          rowData={shown}
          columnDefs={columnDefs}
          defaultColDef={dashboardDefaultColDef}
          rowHeight={DASHBOARD_ROW_HEIGHT}
          headerHeight={32}
          suppressCellFocus
          /* 행을 누르면 그 구간의 선적 목록으로 — 권한이 없으면 핸들러가 안 와서 커서도 안 바뀐다 */
          onRowClicked={onRowDrill ? (e) => onRowDrill(e.data) : undefined}
        />
      </Box>
    </>
  );
};

// ─────────────────────────────────────────
// 품목 성과 — lane 이 "어느 경로" 라면 여기는 "무엇"
// ─────────────────────────────────────────
/*
 * 정렬은 **정시율 오름차순**(못 지킨 것이 위). lane 카드가 "느린 것" 을 묻는 것과 다른 질문이라
 *   줄 세우는 기준도 다르다 — 느린 lane 은 길어서 느릴 수 있지만, 품목은 약속을 못 지킨 쪽이 문제다.
 * 계층 레벨을 올리면 서버가 `item_lv{n}_name` 으로 접어서 준다(품목 코드 칸은 비어 온다).
 */
const ItemsCard = ({ title, rows, grouped, groupLabel, levelSelect, drill, onRowDrill }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const columnDefs = useMemo(() => {
    const cols = [];
    if (grouped) {
      cols.push({ headerName: groupLabel, field: "itemName", flex: 1, minWidth: 140 });
    } else {
      cols.push({ headerName: t("itemCd"), field: "itemCd", width: 110 });
      cols.push({ headerName: t("itemName"), field: "itemName", flex: 1, minWidth: 130 });
    }
    cols.push(
      { headerName: t("stShipments"), field: "shipments", width: 66, type: "rightAligned",
        valueFormatter: (p) => (p.value ?? 0).toLocaleString() },
      { headerName: t("stAvgTransit"), field: "avgTransit", width: 80, type: "rightAligned",
        valueFormatter: (p) => (p.value == null ? "-" : `${d1(p.value)}${t("invDaysUnit")}`) },
      { headerName: t("stOnTimePct"), field: "onTimePct", width: 78, type: "rightAligned",
        valueFormatter: (p) => (p.value == null ? "-" : `${d1(p.value)}%`),
        // lane 카드와 **같은 기준**(50% 미만 위험) — 두 표가 다른 잣대를 쓰면 비교가 안 된다
        cellStyle: (p) => ({
          color: (p.value ?? 100) < 50 ? theme.palette.error.dark : theme.palette.text.primary,
          fontWeight: (p.value ?? 100) < 50 ? 700 : 400, textAlign: "right",
        }) },
    );
    return cols;
  }, [t, theme, grouped, groupLabel]);

  return (
    <>
      <DashboardCardHeader title={title} dense {...drill}>{levelSelect}</DashboardCardHeader>
      <Box className="ag-theme-balham" sx={dashboardGridSx(theme, !!onRowDrill)}>
        <AgGridReact
          theme="legacy"
          rowData={rows ?? []}
          columnDefs={columnDefs}
          defaultColDef={dashboardDefaultColDef}
          rowHeight={DASHBOARD_ROW_HEIGHT}
          headerHeight={32}
          suppressCellFocus
          onRowClicked={onRowDrill ? (e) => onRowDrill(e.data) : undefined}
        />
      </Box>
    </>
  );
};

// ─────────────────────────────────────────
// 진행 중 위험 — 이 화면에서 유일하게 "아직 안 끝난 것" 을 보는 카드
// ─────────────────────────────────────────
/*
 * 다른 카드는 전부 도착분(ARRIVED)을 센다 — 지나간 성적표다. 이 카드만 **지금 떠 있는 배**를 본다.
 *   "지난달 정시율이 나빴다" 가 아니라 **"지금 손쓸 게 있다"** 를 말하는 유일한 자리다.
 * mj 실측: 아직 안 떠난 102 건이 **전부** 출발 예정일 경과, 이동 중 73 건 중 72 건이 도착 예정일 경과.
 */
const InflightCard = ({ title, rows, drill }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const daysUnit = t("invDaysUnit");

  return (
    <>
      <DashboardCardHeader title={title} dense {...drill} />
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column",
                 justifyContent: "space-evenly", gap: 1, py: 0.5 }}>
        {(rows ?? []).map((r) => {
          const pct = r.overduePct ?? 0;
          return (
            <Box key={r.status}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 0.3 }}>
                <Typography sx={{ fontSize: FS.CAPTION, color: "text.primary" }}>
                  {t(`stStatus_${r.status}`)}
                </Typography>
                <Box sx={{ display: "flex", gap: 1, alignItems: "baseline" }}>
                  {/* 지연 평균은 **경과한 건들의 평균**이 아니라 전체 평균이라 0 이 섞인다 — 보조로만 */}
                  <Typography sx={{ fontSize: FS.CAPTION, color: "text.secondary" }}>
                    {r.avgOverdueDays == null ? "-" : `+${d1(r.avgOverdueDays)}${daysUnit}`}
                  </Typography>
                  <Typography sx={{ fontSize: FS.BODY, fontWeight: 700, color: "text.primary" }}>
                    {`${fmtExact(r.overdue)} / ${fmtExact(r.shipments)}`}
                  </Typography>
                </Box>
              </Box>
              {/* 막대는 **예정일 경과 비율** — 전부 경과면 꽉 찬다(mj 의 PLANNED 가 그렇다) */}
              <Box sx={{ height: 8, borderRadius: 1, bgcolor: alpha(theme.palette.text.primary, 0.06) }}>
                <Box sx={{
                  width: `${pct}%`, height: "100%", borderRadius: 1,
                  bgcolor: pct >= 50 ? theme.palette.error.main : theme.palette.warning.main,
                }} />
              </Box>
              <Typography sx={{ fontSize: FS.CAPTION, color: "text.secondary", mt: 0.2, display: "block" }}>
                {`${t("stOverdue")} ${pct}% · ${fmtQty(r.qty)}`}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </>
  );
};

// ─────────────────────────────────────────
// 추이 — 정시율(%) + 평균 운송일수
// ─────────────────────────────────────────
const TrendCard = ({ title, rows, drill }) => {
  const { t } = useTranslation();
  return (
    <>
      <DashboardCardHeader title={title} dense {...drill} />
      {/*
        ★ 차트를 Box 로 한 번 더 감싸면 안 된다 — DashboardComposedChart 가 자체적으로 높이를 잡는데
          중간에 flex 컨테이너가 끼면 ResponsiveContainer 가 높이 0 을 받아 **조용히 아무것도 안 그린다**
          (에러도 경고도 없다). 헤더 다음에 바로 둔다 — 다른 차트 카드들과 같은 구조.
      */}
      <DashboardComposedChart
        data={rows ?? []}
        yAxes={[
          { id: "days", tickFormatter: (v) => `${v}` },
          { id: "pct", orientation: "right", tickFormatter: (v) => `${v}%` },
        ]}
        tooltipFormatter={(v, name) => (name === t("stOnTimePct") ? `${v}%` : `${v}`)}
      >
        {barOrLine({}, { key: "avgTransit", dataKey: "avgTransit", name: t("stAvgTransit"),
          color: SERIES.transit, yAxisId: "days", dflt: "BAR" })}
        {barOrLine({}, { key: "onTimePct", dataKey: "onTimePct", name: t("stOnTimePct"),
          color: SERIES.onTime, yAxisId: "pct", dflt: "LINE" })}
      </DashboardComposedChart>
    </>
  );
};

// ─────────────────────────────────────────
// 메인
// ─────────────────────────────────────────
const SupplyTrendDashboard = ({ viewName }) => {
  const { t } = useTranslation();
  const { isPermitted } = useMenuAccess();
  const reduxDispatch = useDispatch();

  const reduxViewState = reduxUtil.getViewState(viewName);
  const [layoutOpen, setLayoutOpen] = useState(false);
  const [drillHoverKey, setDrillHoverKey] = useState(null);
  const [itemLevel, setItemLevel] = useState(reduxViewState?.itemLevel ?? "");  // 품목 카드에만 적용

  // itemLevel 이 바뀌면 재조회한다 — 롤업은 서버가 한다(공용 DwItemLevelJdbcQueryService)
  const { payload } = useDashboardData(supplyTrendService.get, { itemLevel: itemLevel || undefined });
  const { topN, saveTopN, layout, saveLayout } =
    useDashboardP13n(viewName, { defaultLayout: DEFAULT_LAYOUT });

  useEffect(() => {
    reduxDispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(viewName, { itemLevel })));
  }, [itemLevel, viewName, reduxDispatch]);

  const cardLabels = useMemo(() => ({
    delaySplit: t("stDelaySplitCard"),
    lanes: t("stLanesCard"),
    items: t("stItemsCard"),
    inflight: t("stInflightCard"),
    trend: t("stTrendCard"),
  }), [t]);

  if (!payload) return null;

  const kpis = payload.kpis ?? {};
  const fmtMoney = makeFmtMoney(payload.currencyCd);
  const daysUnit = t("invDaysUnit");

  /*
   * 품목 계층 레벨 — 라벨은 **서버가 해석한 값**(payload.itemLevel) 기준으로 찾는다.
   *   요청값으로 찾으면 서버가 폴백했을 때(없는 레벨을 보냈을 때) 헤더만 거짓말을 한다.
   *   레벨 이름은 테넌트 `z_hrchy_config` 값이라 번역 대상이 아니다 — 받은 문자열을 그대로 쓴다.
   *
   * ★ **최하위 레벨은 목록에서 뺀다**(`slice(0, -1)`).
   *   공용 `DwItemLevelJdbcQueryService.levelColumn()` 은 최하위에서 `null` 을 돌려주고
   *   그러면 **품목 상세**가 나온다("조치 대상이 사라지면 안 된다" 는 공용 규약).
   *   즉 최하위를 골라도 맨 위 "품목 레벨"(미선택)과 **결과가 똑같다** — 선택지가 둘인데 결과가
   *   하나면 "왜 안 묶이지" 로 읽힌다. 빼서 중복을 없앤다.
   *   최하위 판정은 서버와 같은 기준이다(옵션 배열의 **마지막**).
   */
  const levelOptions = (payload.itemLevels ?? []).slice(0, -1).map((lv) => ({
    value: String(lv.value ?? lv.levelCd ?? ""),
    label: lv.label ?? lv.name ?? "",
  }));
  const activeLevel = levelOptions.find((lv) => lv.value === String(payload.itemLevel ?? ""));
  const itemGroupLabel = activeLevel?.label || t("itemLevel");
  const levelSelect = (
    <CompactSelect displayEmpty value={itemLevel} minWidth={104}
      options={[{ value: "", label: t("itemLevel") }, ...levelOptions]}
      onChange={setItemLevel} />
  );

  const drillIf = (key, fn) => (isPermitted(key) ? fn : undefined);

  /*
   * ★ 이 화면의 드릴은 **전부 출하 실적**으로 간다 — 주문 추적이 아니다.
   *   여긴 **선적** 축인데 주문 테이블에는 지연 컬럼이 아예 없어 축이 끊긴다(설계 §B-2).
   *
   * 원칙 두 가지(§B-3):
   *   ② 그 카드가 보여준 **모수를 그대로 재현**한다 — 누른 숫자와 열린 건수가 다르면 드릴은 거짓말이다.
   *   ③ 재현할 수 없으면 **드릴을 걸지 않는다**. 그래서 진행 중 위험·이동 중 재고에는 드릴이 없다
   *     (원본에 `lifecycle_status` 가 없어 "아직 안 떠남/이동 중" 을 조건으로 쓸 수 없다).
   *
   * ★★ 기간은 **집계 창**(`payload.window`)을 **`atd`(실제 출발일) 기준**으로 심는다 ★★
   *   화면 라벨(`payload.period`)은 데이터가 있는 범위라 창보다 좁을 수 있다 — 라벨로 걸면 모수가 달라진다.
   *   날짜 컬럼이 `atd` 여야 하는 이유: 이 화면의 모수는 DW 의 `month_first_date` 인데 그건 ETL 이
   *   atd→etd→ata→eta 로 폴백해 만든 **출발일 계열**이다. mj 실측에서 `atd` 로 걸었을 때만 네 갈래가
   *   전부 일치했다(bothLate 626=626 / etd 619 · ata 666 · eta 658).
   *   기간을 아예 안 심으면 전 기간이 열려 626 자리에 2,217 이 뜬다.
   *
   * ⚠ 조건 노드의 `headerName` 은 화면 라벨이 아니라 **서버 바인딩 키**다(advanced-filter 스킬 §2-2).
   *   `MasterColumn.ShipmentTransaction` 에 없는 키를 보내면 조건이 **조용히 무시돼** 전체가 열린다.
   */
  const openShipmentTxn = (conditions = []) => {
    const w = payload.window ?? payload.period ?? {};
    const from = w.from;   // "yyyy-MM"
    const to = w.to;
    /*
      끝월의 말일 — `new Date(y, m, 0)` 은 "다음 달 0 일" = 이번 달 마지막 날이다.
        숫자 인자 생성자라 로컬 시간대로 만들어진다(문자열 파싱이 아니므로 하루 밀림 없음 — 스킬 §8).
    */
    const lastDay = to ? new Date(Number(to.slice(0, 4)), Number(to.slice(5, 7)), 0).getDate() : null;
    reduxDispatch(reduxUtil.removeViewState({ viewName: DRILL.shipmentTxn }));
    reduxDispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(DRILL.shipmentTxn, {
      // 조건이 없으면 `advancedFilters` 자체를 심지 않는다 — 빈 children 은 받는 쪽이 "필터 있음" 으로 읽는다
      ...(conditions.length ? { advancedFilters: { logicOperator: "AND", children: conditions } } : {}),
      ...(from && lastDay ? {
        selectedSearch: "atd",                                  // 기간을 걸 날짜 컬럼(기본값 eta 를 덮는다)
        period: { start: `${from}-01`, end: `${to}-${String(lastDay).padStart(2, "0")}` },
      } : {}),
      currentPage: 1,
    })));
    reduxDispatch(reduxUtil.updateHistoryState(DRILL.shipmentTxn));
  };

  const eq = (headerName, value, filterType = "string") =>
    ({ filterType, headerName, operator: "equals", values: [value] });

  const openShipmentTxnByBucket = (bucket) => openShipmentTxn([eq("delayType", bucket, "select")]);
  const openShipmentTxnByLane = (row) => openShipmentTxn([
    eq("originLocation", row.origin), eq("destLocation", row.dest)]);
  const openShipmentTxnByItem = (row) => openShipmentTxn([eq("itemCd", row.itemCd)]);
  /*
   * 제목 드릴 — 조건 없이 **집계 창만** 들고 출하 실적으로. 카드 전체를 목록으로 펼치는 입구다.
   *   ★ 모수를 재현할 수 없는 카드(진행 중 위험)에는 **주지 않는다**(원칙 3) —
   *     그 카드는 기간 필터가 없는 시점 상태라 창을 심으면 오히려 다른 집합이 열린다.
   */
  const NO_TITLE_DRILL = new Set(["inflight"]);
  const titleDrill = (cardKey) => {
    if (NO_TITLE_DRILL.has(cardKey)) return undefined;
    const fn = drillIf(DRILL.shipmentTxn, () => openShipmentTxn());
    return fn && {
      onDrill: fn,
      drillTitle: t("shipmentTransaction"),   // 메뉴와 같은 기존 키 재사용(새 키 만들지 않는다)
      onDrillHover: (on) => setDrillHoverKey(on ? cardKey : null),
    };
  };

  const renderCard = (key, drill) => {
    switch (key) {
      case "delaySplit":
        return <DelaySplitCard title={cardLabels.delaySplit} rows={payload.delaySplit} drill={drill}
          onBucketDrill={drillIf(DRILL.shipmentTxn, openShipmentTxnByBucket)} />;
      case "lanes":
        return <LanesCard title={cardLabels.lanes} rows={payload.lanes}
          topN={topN} onChangeTopN={saveTopN} drill={drill}
          onRowDrill={drillIf(DRILL.shipmentTxn, openShipmentTxnByLane)} />;
      case "items":
        return <ItemsCard title={cardLabels.items} rows={payload.items}
          grouped={!!payload.itemGrouped} groupLabel={itemGroupLabel}
          levelSelect={levelSelect} drill={drill}
          /* 롤업 중에는 행 드릴을 끈다 — 그룹 행엔 품목코드가 없어 조건을 만들 수 없다(원칙 3) */
          onRowDrill={payload.itemGrouped ? undefined : drillIf(DRILL.shipmentTxn, openShipmentTxnByItem)} />;
      case "inflight":
        return <InflightCard title={cardLabels.inflight} rows={payload.inflight} drill={drill} />;
      case "trend":
        return <TrendCard title={cardLabels.trend} rows={payload.trend} drill={drill} />;
      default:
        return null;
    }
  };

  const spanOf = (key) => CARD_DEFS.find((c) => c.key === key)?.span ?? 3;
  const minHOf = (key) => CARD_DEFS.find((c) => c.key === key)?.minH ?? 260;
  const visibleOrder = (layout?.order ?? DEFAULT_LAYOUT.order).filter((k) => !layout?.hidden?.[k]);

  const gridRows = visibleOrder.reduce((rows, key) => {
    const s = spanOf(key);
    const last = rows[rows.length - 1];
    if (!last || last.used + s > 6) rows.push({ keys: [key], used: s });
    else { last.keys.push(key); last.used += s; }
    return rows;
  }, []);
  const gridTemplateRows = gridRows
    .map((r) => { const m = Math.max(...r.keys.map(minHOf)); return `minmax(${m}px, ${m}fr)`; })
    .join(" ");

  return (
    <DashboardPage>
      <DashboardTopBar period={payload.period} pinViewName={viewName}>
        {/*
          증감률(-0.5pp 등)의 **비교 기준**을 한 번만 적는다 — KPI 4 장이 같은 기준을 쓰므로
            카드마다 반복하면 시끄럽고, 아예 없으면 "무엇과 비교한 값인지" 를 알 수 없다.
          "전년 대비" 라고 쓰지 않는다 — 창이 12 개월이라 지금은 전년 동기간과 같지만
            창 길이가 바뀌면 틀린 말이 되므로 **실제 기간을 그대로** 보여준다.
        */}
        {payload.comparedTo && (
          <Typography sx={{ fontSize: FS.CAPTION, color: "text.secondary" }}>
            {t("stDeltaBasis", { from: payload.comparedTo.from, to: payload.comparedTo.to })}
          </Typography>
        )}
        <IconButton size="small" onClick={() => setLayoutOpen(true)} sx={{ p: 0.4, color: "text.secondary" }}>
          <TuneIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </DashboardTopBar>

      {/*
        KPI 4 장. 정시율은 **낮을수록 나쁘고**(부호 그대로), 운송일수는 **낮을수록 좋다**(deltaGood 반전).
        직전 창에 도착 건이 없으면 서버가 deltaPct 를 null 로 주고 KpiCard 가 증감 영역을 안 그린다.
      */}
      <DashboardRow kpi columns={4}>
        <KpiCard label={t("stOnTimeRate")}
          value={kpis.onTime?.pct == null ? "-" : `${kpis.onTime.pct}%`}
          deltaPct={kpis.onTime?.deltaPct} deltaUnit="pp"
          /*
            분자·분모에 **라벨을 붙인다** — "509 / 1,369" 만으로는 무엇을 무엇으로 나눈 값인지
              읽는 사람이 알 수 없다(지연 분해에 머리글을 붙인 것과 같은 이유).
            그리고 축약하지 않는다 — "509 / 1K" 로는 옆의 37.2% 가 검산되지 않는다.
          */
          sub={`${t("stBucket_onTime")} ${fmtExact(kpis.onTime?.ontime ?? 0)}`
            + ` / ${t("stArrived")} ${fmtExact(kpis.onTime?.arrived ?? 0)}`}
          onClick={drillIf(DRILL.shipmentTxn, () => openShipmentTxn())} />
        {/*
          계획 LT 를 보조 줄에 붙인다 — 13.1 일이 **느린 건지 계획보다 느린 건지**는 다른 질문이다.
          별도 KPI 카드를 안 만든 이유: "계획 LT 초과" 집합이 "지연" 집합과 **완전히 같아서**
          초과율은 정시율의 여집합이다(서버 주석 참조). 새 정보는 초과의 **크기**뿐이다.
        */}
        <KpiCard label={t("stAvgTransitDays")}
          value={kpis.transit?.avgDays == null ? "-" : `${d1(kpis.transit.avgDays)}${daysUnit}`}
          deltaPct={kpis.transit?.deltaPct}
          deltaGood={(kpis.transit?.deltaPct ?? 0) <= 0}
          sub={kpis.transit?.plannedDays == null ? undefined
            : `${t("stPlannedLt")} ${d1(kpis.transit.plannedDays)}${daysUnit}`
              + ` · ${kpis.transit.gapDays > 0 ? "+" : ""}${d1(kpis.transit.gapDays)}${daysUnit}`}
          onClick={drillIf(DRILL.shipmentTxn, () => openShipmentTxn())} />
        {/*
          창고에도 계획 잔량에도 안 잡히지만 **이미 돈이 나간** 재고 — 이 화면만 본다.
          ★ **드릴을 걸지 않는다**(설계 원칙 3) — 이 값은 기간 집계가 아니라 **지금 상태**인데,
            원본 `z_shipment_txn` 에는 `lifecycle_status` 가 없어 "이동 중" 을 조건으로 쓸 수 없다.
            모수를 재현 못 하는 드릴은 누른 숫자와 다른 목록을 열어 거짓말이 된다.
        */}
        <KpiCard label={t("stInTransit")}
          value={fmtQty(kpis.inTransit?.qty ?? 0)}
          sub={`${kpis.inTransit?.shipments ?? 0}${t("stShipmentsUnit")} · ${fmtMoney(kpis.inTransit?.amt ?? 0)}`} />
        <KpiCard label={t("stShippedQty")}
          value={fmtQty(kpis.shipped?.qty ?? 0)}
          sub={fmtMoney(kpis.shipped?.amt ?? 0)}
          onClick={drillIf(DRILL.shipmentTxn, () => openShipmentTxn())} />
      </DashboardRow>

      <Box sx={{
        flex: 1, minHeight: 0, overflow: "auto",
        display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gridTemplateRows,
        gridAutoFlow: "row dense", gap: 1.5,
      }}>
        {visibleOrder.map((key) => {
          const drill = titleDrill(key);
          return (
            <Card key={key}
              sx={{
                gridColumn: `span ${spanOf(key)}`, minHeight: 0,
                transition: CARD_HOVER_TRANSITION,
                ...(drillHoverKey === key ? CARD_HOVER_SX : {}),
              }}>
              {renderCard(key, drill)}
            </Card>
          );
        })}
      </Box>

      <DashboardLayoutModal
        open={layoutOpen}
        onClose={() => setLayoutOpen(false)}
        cards={CARD_DEFS.map((c) => ({ key: c.key, label: cardLabels[c.key] }))}
        layout={layout ?? DEFAULT_LAYOUT}
        onSave={saveLayout}
      />
    </DashboardPage>
  );
};

export default SupplyTrendDashboard;
